/**
 * Versionamento proativo da SPA.
 *
 * Lê a versão embutida no <meta name="aawz-version"> do index.html (injetada
 * pelo Flask via render_template) e compara periodicamente com a versão
 * servida pelo backend em /server/spa-version/frontend. Quando há
 * divergência, emite MODAL.NEW_VERSION_AVAILABLE — consumido por
 * the-new-version-modal.vue.
 *
 * Disparos:
 *  - startHeartbeat(): setInterval de 5min com a aba ativa.
 *  - bindVisibilityListener(): refaz check quando a aba volta a ficar
 *    visível (respeitando TTL de 5min).
 *  - chunkErrorHandler.handleChunkError(): caso reativo legado, emite
 *    MODAL.NEW_VERSION_AVAILABLE com { autoReload: true } direto, sem
 *    passar por aqui.
 *
 * Dismissal:
 *  - Se o usuário fechar o modal proativo, marcamos userDismissed = true.
 *  - Enquanto userDismissed estiver ligado, heartbeat/visibility não
 *    emitem mais o modal. O caso reativo (autoReload=true) ignora a flag
 *    — chunk error sempre dispara o modal.
 *
 * Fixes AAWZ-WEB-3VT (camada proativa).
 */

import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { getSpaVersion, VERSION_CHECK_ENDPOINT } from '@services/apiVersionCheck'

export const VERSION_CHECK_TTL_MS = 5 * 60 * 1000
export const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000
// Re-exporta para back-compat — callers antigos importavam daqui.
export { VERSION_CHECK_ENDPOINT }
// Alias retro-compatível para callers antigos. Use MODAL.NEW_VERSION_DISMISSED
// em código novo.
export const VERSION_CHECK_DISMISSED_EVENT = MODAL.NEW_VERSION_DISMISSED
export const VERSION_META_NAME = 'aawz-version'
const UNKNOWN_VERSION_MARKER = 'unknown'
// Quando o template é servido fora do Flask (ex.: `npm run dev` sem proxy),
// o {{ aawz_version }} Jinja não é interpolado e chega literal — tratamos
// como "sem versão embutida" para não disparar modal em dev.
const JINJA_UNRESOLVED_MARKER = '{{'

// Janela mínima entre console.warn de erro de rede. Sem isso, uma aba aberta
// a noite toda com backend fora pode logar até 288 warnings (heartbeat 5min).
const WARN_THROTTLE_MS = 60 * 60 * 1000

let lastCheck = { ts: 0, result: null }
let userDismissed = false
let heartbeatTimer = null
let visibilityListenerBound = false
let dismissListenerBound = false
let dismissHandler = null
let visibilityHandler = null
let lastWarnTs = 0

export function getBundledVersion() {
  if (typeof document === 'undefined' || !document.querySelector) return null
  const meta = document.querySelector(`meta[name="${VERSION_META_NAME}"]`)
  if (!meta) return null
  const content = meta.getAttribute('content')
  if (!content || content === UNKNOWN_VERSION_MARKER) return null
  if (content.includes(JINJA_UNRESOLVED_MARKER)) return null
  return content
}

async function fetchLatestVersion() {
  try {
    const r = await getSpaVersion()
    if (r && r.status === 200 && r.value && r.value.versao) {
      return r.value.versao
    }
  } catch (err) {
    const now = Date.now()
    // lastWarnTs === 0 significa "nunca alertou" — primeira falha sempre loga,
    // mesmo com Date.now() pequeno (ex.: testes com timestamps mockados).
    if (lastWarnTs === 0 || now - lastWarnTs >= WARN_THROTTLE_MS) {
      lastWarnTs = now
      // eslint-disable-next-line no-console
      console.warn('[version-check] Falha ao consultar versão:', err && err.message)
    }
  }
  return null
}

/**
 * Compara a versão embutida no HTML com a versão atual servida pelo backend.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.force=false] — ignora o TTL e força um refetch
 * @returns {Promise<{
 *   isOutdated: boolean,
 *   bundledVersion: string|null,
 *   latestVersion: string|null,
 *   cached: boolean,
 *   error?: boolean
 * }>}
 *
 * Shape do retorno:
 *  - bundled ausente (dev sem Flask, meta missing): isOutdated=false, latestVersion=null
 *  - request OK: isOutdated=(bundled !== latest), cached refletindo TTL hit/miss
 *  - request com erro: isOutdated=false, latestVersion=null, error=true (NÃO popula cache)
 */
export async function checkVersion({ force = false } = {}) {
  const bundledVersion = getBundledVersion()
  if (!bundledVersion) {
    return { isOutdated: false, bundledVersion: null, latestVersion: null, cached: false }
  }
  const now = Date.now()
  if (!force && lastCheck.result && (now - lastCheck.ts) < VERSION_CHECK_TTL_MS) {
    return { ...lastCheck.result, cached: true }
  }
  const latestVersion = await fetchLatestVersion()
  if (latestVersion) {
    const result = {
      isOutdated: bundledVersion !== latestVersion,
      bundledVersion: bundledVersion,
      latestVersion: latestVersion,
      cached: false,
    }
    lastCheck = { ts: now, result: result }
    return result
  }
  return {
    isOutdated: false,
    bundledVersion: bundledVersion,
    latestVersion: null,
    cached: false,
    error: true,
  }
}

function onUserDismissed() {
  userDismissed = true
}

function bindDismissListener() {
  if (dismissListenerBound) return
  dismissListenerBound = true
  dismissHandler = onUserDismissed
  GlobalBus.$on(MODAL.NEW_VERSION_DISMISSED, dismissHandler)
}

function unbindDismissListener() {
  if (!dismissListenerBound) return
  if (dismissHandler) {
    GlobalBus.$off(MODAL.NEW_VERSION_DISMISSED, dismissHandler)
  }
  dismissHandler = null
  dismissListenerBound = false
}

export async function notifyIfOutdated({ force = false, autoReload = false } = {}) {
  bindDismissListener()
  if (!autoReload && userDismissed) {
    return { isOutdated: false, dismissed: true }
  }
  const result = await checkVersion({ force: force })
  if (result.isOutdated) {
    GlobalBus.$emit(MODAL.NEW_VERSION_AVAILABLE, { autoReload: autoReload })
  }
  return result
}

export function dismissProactiveCheck() {
  userDismissed = true
}

export function startHeartbeat() {
  if (heartbeatTimer) return
  bindDismissListener()
  heartbeatTimer = setInterval(() => {
    notifyIfOutdated()
  }, HEARTBEAT_INTERVAL_MS)
}

export function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    notifyIfOutdated()
  }
}

export function bindVisibilityListener() {
  if (visibilityListenerBound) return
  if (typeof document === 'undefined' || !document.addEventListener) return
  visibilityListenerBound = true
  bindDismissListener()
  visibilityHandler = onVisibilityChange
  document.addEventListener('visibilitychange', visibilityHandler)
}

export function unbindVisibilityListener() {
  if (!visibilityListenerBound) return
  if (visibilityHandler && typeof document !== 'undefined' && document.removeEventListener) {
    document.removeEventListener('visibilitychange', visibilityHandler)
  }
  visibilityHandler = null
  visibilityListenerBound = false
}

export function _resetForTests() {
  lastCheck = { ts: 0, result: null }
  userDismissed = false
  lastWarnTs = 0
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
  unbindVisibilityListener()
  unbindDismissListener()
}
